import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import { get, API } from '../lib/api';
import TopBar from '../components/layout/TopBar';
import SectionCard from '../components/widgets/SectionCard';
import KpiCard from '../components/widgets/KpiCard';
import EntriesDrillDown from '../components/EntriesDrillDown';
import { Button } from '../components/ui/button';
import { fmtBRL, fmtPct } from '../lib/format';
import { Layers, PieChart, TrendingDown, Info, ChevronRight, FileDown, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Controladoria › Gestão de Despesas
 *  - KPIs clicáveis para filtrar o ranking (Fixa / Variável / Sem classificação)
 *  - Botão "Gerar Relatório" baixa PDF com Fixa vs Variável
 */
export default function ControladoriaDespesas() {
  const { companyFilter, competence, periodMode, competenceFrom, competenceTo } = useApp();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drillItem, setDrillItem] = useState(null);
  const [kind, setKind] = useState(null); // null | 'FIXA' | 'VARIAVEL' | 'SEM'
  const [busy, setBusy] = useState(false);

  const cid = companyFilter === 'all' ? null : companyFilter;

  const params = useMemo(() => {
    const p = { limit: 30 };
    if (cid) p.company_id = cid;
    if (periodMode === 'range') {
      p.competence_from = competenceFrom;
      p.competence_to = competenceTo;
    } else {
      p.competence = competence;
    }
    if (kind) p.kind = kind;
    return p;
  }, [cid, periodMode, competence, competenceFrom, competenceTo, kind]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await get('/controladoria/despesas-summary', params);
        setSummary(r);
      } finally {
        setLoading(false);
      }
    })();
  }, [params]);

  const toggleKind = (k) => setKind((cur) => (cur === k ? null : k));

  const downloadReport = async () => {
    setBusy(true);
    try {
      const p = {};
      if (cid) p.company_id = cid;
      if (periodMode === 'range') {
        p.competence_from = competenceFrom;
        p.competence_to = competenceTo;
      } else {
        p.competence = competence;
      }
      const res = await axios.get(`${API}/reports/despesas-breakdown-pdf`, {
        params: p,
        responseType: 'blob',
        withCredentials: true,
        timeout: 120000,
      });
      const disp = res.headers['content-disposition'] || '';
      const match = /filename="?([^"]+)"?/i.exec(disp);
      const filename = match ? match[1] : `relatorio-despesas.pdf`;
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Relatório gerado');
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Erro ao gerar relatório';
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  const empty = !loading && summary && summary.total_despesas === 0;

  const rankingTitle =
    kind === 'FIXA' ? 'Ranking de Despesas Fixas'
      : kind === 'VARIAVEL' ? 'Ranking de Despesas Variáveis'
        : kind === 'SEM' ? 'Ranking de Despesas Sem Classificação'
          : 'Ranking de Despesas';

  const rankingSubtitle =
    kind
      ? `Mostrando apenas ${
          kind === 'FIXA' ? 'Fixas' : kind === 'VARIAVEL' ? 'Variáveis' : 'Sem Classificação'
        } · Clique em "Limpar filtro" para ver tudo`
      : 'Ordenado por valor · Clique numa linha para ver o discriminado';

  return (
    <div className="fade-in">
      <TopBar
        title="Gestão de Despesas"
        subtitle="Controladoria — classificação Fixa vs Variável e ranking por categoria/centro de custo"
        action={
          <Button
            onClick={downloadReport}
            disabled={busy || loading}
            className="btn-gold rounded-md gap-2 px-4"
            data-testid="btn-gerar-relatorio-despesas"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />}
            {busy ? 'Gerando PDF…' : 'Gerar Relatório'}
          </Button>
        }
      />

      <div className="px-8 py-7 space-y-5" data-testid="controladoria-despesas">
        {/* KPIs clicáveis */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <KpiCard
            label="Despesa Total"
            value={summary?.total_despesas || 0}
            accent="#EF4444"
            icon={Layers}
            testid="desp-total"
            highlight={kind === null}
            onClick={() => setKind(null)}
          />
          <KpiCard
            label="Despesa Fixa"
            value={summary?.total_fixa || 0}
            accent="#F59E0B"
            icon={TrendingDown}
            testid="desp-fixa"
            pctOfRevenue={summary?.pct_fixa}
            highlight={kind === 'FIXA'}
            onClick={() => toggleKind('FIXA')}
          />
          <KpiCard
            label="Despesa Variável"
            value={summary?.total_variavel || 0}
            accent="#8B5CF6"
            icon={PieChart}
            testid="desp-variavel"
            pctOfRevenue={summary?.pct_variavel}
            highlight={kind === 'VARIAVEL'}
            onClick={() => toggleKind('VARIAVEL')}
          />
        </div>

        {/* Aviso de não-classificadas */}
        {summary && summary.total_sem_classificacao > 0 && (
          <div
            className="flex items-start gap-3 px-4 py-3 bg-amber-950/20 border border-amber-700/40 rounded-md cursor-pointer hover:bg-amber-950/30 transition-colors"
            onClick={() => toggleKind('SEM')}
            role="button"
            tabIndex={0}
            data-testid="desp-sem-class-banner"
          >
            <Info size={16} className="text-amber-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-[13px] text-amber-100">
                <strong>{fmtBRL(summary.total_sem_classificacao)}</strong> ({fmtPct(summary.pct_sem_classificacao)}) em despesas ainda <strong>não classificadas</strong> como Fixa ou Variável.
                {kind === 'SEM' && <span className="ml-2 pill pill-warning text-[10px]">filtro ativo</span>}
              </div>
              <div className="text-[11.5px] text-amber-100/70 mt-1">
                Vá em <strong>Lançamentos → editar</strong> cada DESPESA e escolha o campo "Classificação da Despesa" para melhorar essa análise. Ou clique aqui para filtrar o ranking abaixo.
              </div>
            </div>
          </div>
        )}

        {/* Ranking */}
        <SectionCard
          title={rankingTitle}
          subtitle={rankingSubtitle}
          testid="desp-ranking-card"
          action={
            kind && (
              <Button
                variant="ghost"
                size="sm"
                className="text-zinc-400 hover:text-zinc-100 gap-1.5"
                onClick={() => setKind(null)}
                data-testid="btn-clear-kind-filter"
              >
                <X size={12} /> Limpar filtro
              </Button>
            )
          }
        >
          {loading ? (
            <div className="p-8 text-center text-zinc-500 text-sm">Carregando…</div>
          ) : empty ? (
            <div className="p-8 text-center text-zinc-500 text-sm">
              Nenhuma despesa encontrada neste período.
            </div>
          ) : (summary?.ranking || []).length === 0 ? (
            <div className="p-8 text-center text-zinc-500 text-sm">
              Nenhuma despesa deste tipo neste período.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full ceap-table" data-testid="desp-ranking-table">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>#</th>
                    <th>Categoria / Centro de Custo</th>
                    <th className="text-center">Qtd. Lançamentos</th>
                    <th className="text-right">Total</th>
                    <th className="text-right">{kind ? '% do Grupo' : '% do Total'}</th>
                    <th style={{ width: 32 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {(summary?.ranking || []).map((r, i) => (
                    <tr
                      key={`${r.category_id || r.cost_center_id || r.name}-${i}`}
                      className="cursor-pointer hover:bg-zinc-900/40"
                      onClick={() => setDrillItem(r)}
                      data-testid={`desp-row-${i}`}
                    >
                      <td className="text-zinc-500 font-mono text-xs">{i + 1}</td>
                      <td className="text-zinc-100 font-medium">{r.name}</td>
                      <td className="text-center font-mono text-zinc-400">{r.count}</td>
                      <td className="text-right font-mono red-text">{fmtBRL(r.total)}</td>
                      <td className="text-right font-mono text-zinc-300">{fmtPct(r.pct)}</td>
                      <td className="text-zinc-600">
                        <ChevronRight size={14} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>
      </div>

      {/* Drill-down */}
      <EntriesDrillDown
        open={!!drillItem}
        onClose={() => setDrillItem(null)}
        type="DESPESA"
        competence={periodMode === 'range' ? competenceTo : competence}
        companyId={cid}
        title={drillItem ? `Despesas — ${drillItem.name}` : ''}
        filterCategoryId={drillItem?.category_id}
        filterCostCenterId={drillItem?.cost_center_id}
        filterName={drillItem?.name}
      />
    </div>
  );
}
